// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { Space, Modal, Spin } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';

import './FileImport.scss';

interface FileImportProps {
    onSubmit: (formData: FormData) => Promise<void>;
    className?: string;
    buttonLabel?: string;
    footerLabel?: string;
    header: ReactNode;
    drawerTitle?: string;
    uploadTitle?: string;
    uploadSubtitle?: string;
    accept?: string;
    name?: string;
    children?: ReactNode;
    preValidate?: boolean;
}

const FileImport = ({
    onSubmit,
    header,
    className,
    drawerTitle = '',
    uploadTitle = '',
    uploadSubtitle = '',
    buttonLabel = '',
    footerLabel = '',
    accept = '.zip',
    name = 'file',
    children,
    preValidate = true,
}: FileImportProps): ReactElement => {
    const i = useLocalMessage();

    const [disabled, setDisabled] = useState(true);
    const [file, setFile] = useState<UploadFile<any>>();
    const [buttonLoading, setButtonLoading] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setDisabled(!(preValidate && file));
    }, [file, preValidate]);

    return (
        <>
            <THDrawer
                title={drawerTitle}
                className={classList('file-import__drawer', 'drawer-form', className)}
                triggerElem={
                    <THButton className="file-import__trigger-button" loading={buttonLoading}>
                        {buttonLabel}
                    </THButton>
                }
                width="35vw"
                afterVisibleChange={(visible) => {
                    if (!visible) {
                        setFile(undefined);
                    }
                }}
            >
                {(toggleDrawer) => (
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={disabled}
                                footerText={footerLabel}
                                onSubmit={() => {
                                    if (file) {
                                        const formData = new FormData();
                                        setVisible(true);
                                        formData.set(name, file as any);
                                        toggleDrawer();
                                        setButtonLoading(true);

                                        onSubmit(formData).then(() => {
                                            setVisible(false);
                                            setButtonLoading(false);
                                            setFile(undefined);
                                        });
                                    }
                                }}
                            />
                        }
                    >
                        <Space direction="vertical" className="file-import__container" size="large">
                            {header}
                            <Space direction="vertical" className="file-import__upload">
                                <span className="file-import__upload-title">{uploadTitle}</span>
                                {children}
                                <UploadSingleFile
                                    text={i('taxonomy.import.upload.content')}
                                    value={file}
                                    onChange={(data): void => setFile(data)}
                                    accept={accept}
                                />
                                <span>{uploadSubtitle}</span>
                            </Space>
                        </Space>
                    </THForm.Layout>
                )}
            </THDrawer>

            <Modal visible={visible} closable={false} footer={null} className="file-import__modal">
                <Spin className="file-import__modal-spin" />
                <p className="file-import__modal-label">Loading...</p>
            </Modal>
        </>
    );
};

export default FileImport;
